import { Droplet, Flame, Gauge, Zap, type LucideIcon } from 'lucide-react';

export type UtilityChartKind = 'area' | 'bar' | 'line';

export interface UtilityPresentation {
  icon: LucideIcon;
  color: string;
  chartKind: UtilityChartKind;
}

/** Hints advertised by the handler action meta (x-ui) — the dynamic source. */
export interface UtilityPresentationHint {
  /** Handler-advertised kind: 'water' | 'electric' | 'gas' | ... */
  utilityKind?: string;
  /** Explicit chart-kind override from the handler. */
  chartKind?: string;
  /** Explicit colour override from the handler. */
  color?: string;
}

const WATER_COLOR = '#2563eb';
const ELECTRICITY_COLOR = '#eab308';
const GAS_COLOR = '#f97316';
const DEFAULT_COLOR = '#64748b';

// Visual identity per utility kind. Electricity → bars; water → rounded line.
const KIND_PRESENTATION: Record<string, UtilityPresentation> = {
  water: { icon: Droplet, color: WATER_COLOR, chartKind: 'line' },
  electric: { icon: Zap, color: ELECTRICITY_COLOR, chartKind: 'bar' },
  electricity: { icon: Zap, color: ELECTRICITY_COLOR, chartKind: 'bar' },
  gas: { icon: Flame, color: GAS_COLOR, chartKind: 'bar' },
};

function isChartKind(v: unknown): v is UtilityChartKind {
  return v === 'area' || v === 'bar' || v === 'line';
}

function presentationFromName(name: string): UtilityPresentation {
  const n = name.toLowerCase();
  if (n.includes('water')) return { icon: Droplet, color: WATER_COLOR, chartKind: 'line' };
  if (n.includes('electric')) return { icon: Zap, color: ELECTRICITY_COLOR, chartKind: 'bar' };
  if (n.includes('gas')) return { icon: Flame, color: GAS_COLOR, chartKind: 'bar' };
  return { icon: Gauge, color: DEFAULT_COLOR, chartKind: 'area' };
}

/**
 * Visual identity for a utility. Prefers the handler-advertised kind/overrides
 * (dynamic, from x-ui) so the home widget and the detail view stay aligned;
 * falls back to a name heuristic when nothing is advertised.
 */
export function getUtilityPresentation(
  utilityName: string,
  hint?: UtilityPresentationHint,
): UtilityPresentation {
  const base =
    (hint?.utilityKind ? KIND_PRESENTATION[hint.utilityKind.toLowerCase()] : undefined) ??
    presentationFromName((utilityName ?? '').trim());

  return {
    icon: base.icon,
    color: hint?.color || base.color,
    chartKind: isChartKind(hint?.chartKind) ? hint.chartKind : base.chartKind,
  };
}

export function utilityIconBg(color: string): string {
  return `${color}1f`;
}
